/* WizardCaseHeader — iter101.60
 *
 * New-aligner-case wizard header. Composes the shared
 * /components/shared/CommandHeaderShell so the wizard matches the
 * unified "Command Bar" grammar used across Doctor Case Detail,
 * Doctor Patient Profile, and Planner Case Detail (iter101.59).
 *
 * Wizard-specific concerns handled inside this file:
 *   · Placeholder identity when no patient has been picked yet
 *     (Step 1) — shell renders "New aligner case" title, no chip
 *     strip, no assignment stack.
 *   · Step 1 commitments are echoed as chips as soon as they're
 *     made (planning mode, scan source, compliance, MFG speed).
 *   · "Change patient" link sits inside the demographics suffix.
 *   · "Discard draft" surfaces as a rose destructive action inside
 *     the right slot.
 *   · Step indicator (`children`) is rendered under the header
 *     card, outside the shell — same layer as CaseDetail's status
 *     row so the visual hierarchy stays predictable.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
    Buildings, Phone, PencilSimple, Sparkle, Camera, X, UserPlus,
    ScanSmiley, ShieldCheck, Timer, Hash, Stethoscope,
} from "@phosphor-icons/react";
import PatientAvatar from "@/components/patient/PatientAvatar";
import { CaseStatusBadge } from "@/components/shared/CaseStatusBadge";
import CommandHeaderShell from "@/components/shared/CommandHeaderShell";


function humanPatientId(p) {
    if (!p) return null;
    if (p.human_id) return p.human_id;
    if (p.code) return p.code;
    if (p.id) return `INV-PT-${p.id.replace(/-/g, "").slice(0, 6).toUpperCase()}`;
    return null;
}


export default function WizardCaseHeader({
    /* Identity */
    patient = null,
    titleFallback = "New aligner case",
    clinicName = null,
    /* Case-scoped fields */
    caseCode = null,
    caseStatus = null,
    planningMode = "",
    /* iter101.17 — Step 1 selection strip */
    scanSource = "",
    coeSupportCompliance = null,
    priorityLabel = "",
    priorityCode = "",
    priorityTatDays = null,
    /* Scan-visit resume */
    isScanVisitResume = false,
    scanResumeFileCount = 0,
    scanResumeTechName = "",
    /* Actions */
    /* iter101.81 — Jump-to-Step-1 shortcut fired when the doctor
       clicks the compact "Case setup · X of 4 picked" summary chip
       on Steps 2–6. Optional — falls back to a non-clickable chip
       when unwired. */
    onEditCaseSetup = null,
    busy = false,
    /* iter101.79 — Compact mode. On Steps 2–6 the doctor has already
       committed to all four Step-1 choices; the header no longer
       needs to advertise each one. `compact=true` collapses the
       five individual chips (Case ID + Planning + Scan + Compliance
       + Mfg) into a single "X of 4 picked" summary chip so the
       header takes ~40% less vertical space and downstream steps
       get more content height. Case ID is kept as its own chip
       because it's the only piece the doctor might want to copy. */
    compact = false,
    /* Step indicator slot */
    children = null,
}) {
    const idStr = humanPatientId(patient);
    const headerName = patient?.name || titleFallback;
    const ageStr = patient?.age ? `${patient.age} yr` : null;
    const rawGender = patient?.gender || patient?.sex;
    const genderStr = rawGender
        ? rawGender.charAt(0).toUpperCase() + rawGender.slice(1)
        : null;

    /* Only render the meta strip (chips + status) when there's a real
       case in flight. Brand-new Step 1 with no patient / no draft
       code yet renders just the placeholder identity band. */
    const showMetaStrip = !!patient || !!caseCode;

    /* ── Avatar slot — either the real patient (xl / 64px with teal
         ring) or a soft placeholder tile matching the doctor portal's
         empty-state pattern. ── */
    const avatarNode = patient ? (
        <PatientAvatar
            patient={patient}
            size="xl"
            className="ring-1 ring-teal-200/60 ring-offset-2 ring-offset-card"
        />
    ) : (
        <div
            className="w-16 h-16 rounded-full bg-secondary text-muted-foreground/50 grid place-items-center ring-1 ring-teal-200/60 ring-offset-2 ring-offset-card"
            data-testid="wizard-case-header-avatar-placeholder"
            aria-label="No patient selected yet"
        >
            <UserPlus size={26} weight="duotone" />
        </div>
    );

    /* Demographics — age · gender · ID (auto-mono). Prefixed with
       "New aligner case" hint when there's no patient yet. */
    const demographics = patient
        ? [ageStr, genderStr, idStr].filter(Boolean)
        : [];

    /* Demographics suffix — "Open profile" deep-link.
       iter101.83 — "Change" (swap-patient) shortcut removed per
       user direction. Doctors can restart the wizard from
       /doctor/cases/new if they picked the wrong patient. */
    const demographicsSuffix = patient?.id ? (
        <Link
            to={`/doctor/patients/${patient.id}`}
            className="text-primary hover:underline font-semibold"
            data-testid="case-wizard-patient-profile-link"
        >
            Open profile
        </Link>
    ) : (
        <span className="text-muted-foreground/80">Pick a patient below to get started</span>
    );

    /* Assignment mini-stack — Phone + Clinic. Only rendered when at
       least one is present so the shell's vertical divider doesn't
       hover over an empty column. */
    const assignments = [];
    if (patient?.phone) {
        assignments.push({
            label: "Contact",
            value: patient.phone,
            icon: Phone,
            testid: "case-wizard-header-phone",
            title: `Call ${patient.name}`,
        });
    }
    if (clinicName) {
        assignments.push({
            label: "Clinic",
            value: clinicName,
            icon: Buildings,
            testid: "case-wizard-header-clinic",
            title: clinicName,
        });
    }

    /* iter101.81 — Right slot removed. Discard-draft was redundant
       with the wizard's Cancel button in the sticky footer. Cancel
       is closer to the doctor's natural progress-flow eye path, so
       we consolidated destructive actions there. */
    const rightSlot = null;

    /* ── Chip strip — Step 1 commitments echoed as data cells. Hidden
         until there's a case in flight. Cells are omitted when their
         data isn't picked yet so the row stays honest instead of
         padded with "—".
         iter101.79 — Compact mode collapses everything except the
         Case ID into a single "X of 4 picked" summary chip. ── */
    const chips = [];
    if (showMetaStrip) {
        if (caseCode) {
            chips.push({
                icon: Hash,
                label: "Case ID",
                value: caseCode,
                mono: true,
                testid: "case-wizard-draft-code",
                title: caseCode,
            });
        }
        if (compact) {
            /* Summary chip — tally the four Step-1 commitments.
               iter101.81 — Clickable when `onEditCaseSetup` is
               wired: doctor taps the chip to jump straight back
               to Step 1 for a review/change without touching the
               stepper. Custom `render` so we can host a full
               <button> instead of the read-only <DataCell>. */
            const scanPicked = scanSource === "upload" || scanSource === "io_service";
            const planPicked = planningMode === "doctor" || planningMode === "coe";
            const compPicked = coeSupportCompliance === true || coeSupportCompliance === false;
            const speedPicked = !!(priorityCode || "").trim();
            const done = [scanPicked, planPicked, compPicked, speedPicked].filter(Boolean).length;
            const allDone = done === 4;
            const valueTone = allDone ? "text-emerald-800" : "text-slate-700";
            const iconTone = allDone ? "text-emerald-700" : "text-teal-700/80";
            const summaryContent = (
                <>
                    <div className="flex items-center gap-1 mb-1">
                        <Sparkle size={10} weight="fill" className={iconTone} />
                        <span className="text-[9px] font-bold tracking-[0.14em] uppercase text-teal-800/70">
                            Case setup
                        </span>
                    </div>
                    <div className={`text-[11.5px] font-semibold leading-tight truncate ${valueTone}`}>
                        {done} of 4 picked
                    </div>
                </>
            );
            chips.push({
                render: onEditCaseSetup ? (
                    <button
                        type="button"
                        onClick={onEditCaseSetup}
                        className="w-full text-left px-3.5 py-2.5 min-w-0 focus:outline-none rounded-md hover:bg-teal-50/70 focus:ring-2 focus:ring-primary/30 transition-colors group"
                        data-testid="case-wizard-setup-summary"
                        title={allDone
                            ? "Case setup complete — click to review Step 1"
                            : `${done} of 4 Step 1 commitments made — click to finish`}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">{summaryContent}</div>
                            <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                Edit ›
                            </span>
                        </div>
                    </button>
                ) : (
                    <div className="px-3.5 py-2.5 min-w-0" data-testid="case-wizard-setup-summary" title={allDone ? "All Step 1 commitments made" : `${done} of 4 Step 1 commitments made — go back to Step 1 to finish`}>
                        {summaryContent}
                    </div>
                ),
            });
        } else if (planningMode === "doctor") {
            chips.push({
                icon: PencilSimple,
                label: "Planning",
                value: "Self-planned",
                testid: "case-planning-mode-self",
                title: "The doctor authored the treatment plan on this case",
            });
        } else if (planningMode === "coe") {
            chips.push({
                icon: Sparkle,
                label: "Planning",
                value: "Invisique™ Team",
                testid: "case-planning-mode-coe",
                title: "Invisique™ Planning Team authored the treatment plan",
            });
        } else {
            chips.push({
                icon: Sparkle,
                label: "Planning",
                value: "Not picked",
                tone: "slate",
                testid: "case-wizard-planning-empty",
                title: "Pick a planning mode in Step 1",
            });
        }
        /* iter101.17 — Scan source chip.
           iter101.69 — Always render this cell so the header advertises
           all four Step-1 commitments up-front. Doctors see "Not picked"
           until they answer, exactly like Planning below.
           iter101.79 — Skipped entirely in compact mode. */
        if (!compact) {
            if (scanSource === "io_service") {
                chips.push({
                    icon: ScanSmiley,
                    label: "Scan source",
                    value: "IO Scans by Invisique™",
                    testid: "case-wizard-scan-source-io",
                    title: "IO Scans by Invisique™",
                });
            } else if (scanSource === "upload") {
                chips.push({
                    icon: ScanSmiley,
                    label: "Scan source",
                    value: "Doctor-uploaded",
                    testid: "case-wizard-scan-source-upload",
                    title: "Doctor-uploaded scans",
                });
            } else {
                chips.push({
                    icon: ScanSmiley,
                    label: "Scan source",
                    value: "Not picked",
                    tone: "slate",
                    testid: "case-wizard-scan-source-empty",
                    title: "Pick a scan source in Step 1",
                });
            }
        }
        /* iter101.69 — Compliance always visible with "Not picked"
           placeholder for parity with Planning + Scan source.
           iter101.79 — Skipped in compact mode. */
        if (!compact && coeSupportCompliance === true) {
            chips.push({
                icon: ShieldCheck,
                label: "Compliance",
                value: "Invisique™ manages",
                tone: "emerald",
                testid: "case-wizard-compliance-invisique",
                title: "Invisique™ manages patient compliance",
            });
        } else if (!compact && coeSupportCompliance === false) {
            chips.push({
                icon: Stethoscope,
                label: "Compliance",
                value: "Self-managed",
                tone: "slate",
                testid: "case-wizard-compliance-self",
                title: "Doctor is self-managing patient compliance",
            });
        } else if (!compact) {
            chips.push({
                icon: ShieldCheck,
                label: "Compliance",
                value: "Not picked",
                tone: "slate",
                testid: "case-wizard-compliance-empty",
                title: "Pick compliance monitoring in Step 1",
            });
        }
        /* iter101.69 — Manufacturing speed always visible.
           iter101.79 — Skipped in compact mode. */
        if (!compact && (priorityCode || "").trim()) {
            chips.push({
                icon: Timer,
                label: "Manufacturing",
                value: `${priorityLabel || "Standard"}${priorityTatDays ? ` · ${priorityTatDays}d` : ""}`,
                tone: priorityCode === "stat" ? "rose" : priorityCode === "rush" ? "amber" : "neutral",
                testid: "case-wizard-mfg-speed",
                title: priorityTatDays ? `Manufacturing turnaround · ${priorityTatDays}-day TAT` : "Manufacturing turnaround",
            });
        } else if (!compact) {
            chips.push({
                icon: Timer,
                label: "Manufacturing",
                value: "Not picked",
                tone: "slate",
                testid: "case-wizard-mfg-speed-empty",
                title: "Pick a manufacturing speed in Step 1",
            });
        }
        /* Scan-visit resume — surfaced as a compact chip when the
           doctor is picking up after an IO-service scan visit. */
        if (isScanVisitResume) {
            const resumeParts = [];
            if (scanResumeFileCount > 0) {
                resumeParts.push(`${scanResumeFileCount} file${scanResumeFileCount === 1 ? "" : "s"}`);
            }
            if (scanResumeTechName) resumeParts.push(scanResumeTechName);
            chips.push({
                icon: Camera,
                label: "Scan visit",
                value: resumeParts.length > 0 ? resumeParts.join(" · ") : "Completed",
                tone: "emerald",
                testid: "case-wizard-scan-resume-chip",
                title: "This case is being resumed from a completed IO scan visit",
            });
        }
    }

    return (
        <div data-testid="wizard-case-header">
            <CommandHeaderShell
                testId="wizard-case-command-header"
                avatar={avatarNode}
                identityLabel={patient ? "Patient · Case" : "New case"}
                name={headerName}
                nameTitle={headerName}
                demographics={demographics}
                demographicsSuffix={demographicsSuffix}
                assignments={assignments}
                rightSlot={rightSlot}
                statusPill={showMetaStrip ? (
                    <span data-testid="case-wizard-status-pill">
                        <CaseStatusBadge status={caseStatus || "draft"} size="md" portal="doctor" />
                    </span>
                ) : null}
                chips={chips}
            />

            {/* Step indicator — mounted OUTSIDE the header card so the
                shell chrome stays a pure identity band. Same visual
                position as before (below the header). */}
            {children && (
                <div
                    className="mt-3 bg-card border border-border rounded-2xl px-4 py-3 sm:px-5 sm:py-4"
                    data-testid="wizard-case-header-steps"
                >
                    {children}
                </div>
            )}
        </div>
    );
}
